import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

interface SelectProps {
  label?: string;
  helperText?: string;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  options: { value: string | number; label: string }[];
  witchCheck?: boolean;
  value?: string | number;
  onChange: (value: string | number) => void;
  fixedText?: string;
  inputClassName?: string;
  required?: boolean;
}

const Select: React.FC<SelectProps> = ({
  label,
  helperText,
  error,
  placeholder,
  disabled,
  options,
  witchCheck,
  value,
  onChange,
  fixedText,
  inputClassName,
  required = true,
}) => {
  const [open, setOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  const handleSelect = (newValue: string | number) => {
    onChange(newValue);
    setOpen(false);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  return (
    <div
      ref={selectRef}
      className="w-full flex-col justify-start items-start inline-flex relative"
    >
      <div className="w-full flex-col justify-start items-start gap-1 flex">
        {label && (
          <div className="self-stretch justify-start items-start gap-1 inline-flex">
            <div
              className={`text-navy-700 text-subtitle2a ${
                disabled && '!text-grey-300'
              }`}
            >
              {label}
            </div>
            {required && (
              <div
                className={`text-right text-red-400 text-subtitle2a ${
                  disabled && '!text-grey-300'
                }`}
              >
                *
              </div>
            )}
          </div>
        )}

        <div className="self-stretch h-10 flex items-center gap-2 relative">
          <input
            readOnly
            className={clsx(
              'w-full h-full px-3 py-2 bg-white rounded border text-primary placeholder:text-navy-400 text-body2 cursor-pointer',
              'disabled:bg-grey-100 disabled:text-grey-300 disabled:placeholder-grey-300 disabled:border-grey-100 disabled:pointer-events-none',
              error
                ? 'border-red-400 outline-red-400 '
                : 'border-navy-200 outline-primary',
              'focus:outline-1',
              value && !disabled && '!placeholder-primary',
              inputClassName
            )}
            disabled={disabled}
            placeholder={
              fixedText ??
              (value
                ? options.find((opt) => opt.value === value)?.label
                : placeholder)
            }
            onClick={() => !disabled && setOpen((prev) => !prev)}
          />
          <i
            className={clsx(
              'text-[20px] absolute right-3 text-navy-400',
              open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line',
              disabled && '!text-grey-300'
            )}
          />
          {open && !disabled && (
            <div className="absolute top-full mt-1 w-full max-h-56 overflow-auto py-2 bg-white rounded shadow border border-navy-200 z-50">
              {options.map((option) => {
                const isSelected = value === option.value;

                return (
                  <div
                    key={option.value}
                    className={clsx(
                      'w-full flex justify-between items-center px-3 py-2 cursor-pointer text-body2 hover:bg-navy-100',
                      isSelected ? 'text-grey-300' : 'text-navy-700'
                    )}
                    role="presentation"
                    onClick={() => handleSelect(option.value)}
                  >
                    <div>{option.label}</div>
                    {witchCheck && isSelected && (
                      <i className="ri-check-line text-green-400 text-[20px]" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {helperText && (!error || disabled) && (
          <div
            className={`self-stretch pl-1 justify-start items-start gap-2.5 inline-flex text-navy-500 ${
              disabled && '!text-grey-300'
            }`}
          >
            <div className=" text-[12px] leading-none">{helperText}</div>
          </div>
        )}

        {!disabled && error && (
          <div className="self-stretch pl-1 justify-start items-start gap-2.5 inline-flex">
            <div className="text-red-400 text-[12px] leading-none">{error}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Select;
